import { ScrollArea } from "@/components/ui/scroll-area";
import { useCreatorLiteSourceRules } from "@/features/standalone-versions";
import { useGetFoldersQuery, useReorderFoldersMutation } from "@/shared/services";
import { useGetTemplatesQuery } from "@api/static-assets";
import { FolderActions } from "@components/sidebar";
import { cn } from "@lib/cn";
import { useSlidesLibraryStoreShallow } from "@modules/slides-library/shared";
import { SlidesLibrarySidebarSkeleton } from "@modules/slides-library/shared/components/skeleton/slides-library-sidebar-skeleton";
import {
  IconChevronRight,
  IconFolder,
  IconGripVertical,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useScrollToCategory } from "@modules/slides-library/features/template-slides/hooks/use-scroll-to-category";
import type { IStoriesFolder } from "@/shared/types";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  getImportStoriesCategoryLabel,
  getImportStoriesCategoryRank,
} from "../constants";

type FolderWithChildren = IStoriesFolder & { children: FolderWithChildren[] };

const buildNestedFolders = (
  allFolders: IStoriesFolder[],
  parentId: string | null | undefined,
): FolderWithChildren[] => {
  return allFolders
    .filter((folder) => {
      if (parentId === undefined) {
        return folder.parentId === null || folder.parentId === undefined;
      }
      return folder.parentId === parentId;
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((folder) => ({
      ...folder,
      children: buildNestedFolders(allFolders, folder._id),
    }));
};

export const ImportFromStoriesSidebar = () => {
  const { data: foldersData, isLoading: foldersLoading } =
    useGetFoldersQuery();
  const { data: templatesData, isLoading: templatesLoading } =
    useGetTemplatesQuery(undefined);

  // A Creator lite imports from the org's own stories and versions only;
  // Storydoc's templates are not offered to them.
  const isCreatorLite = useCreatorLiteSourceRules();
  const {
    importStoriesSource,
    importStoriesFolderId,
    setImportStoriesSource,
    setImportStoriesFolderId,
    clearImportStoriesSelectedStory,
  } = useSlidesLibraryStoreShallow((s) => ({
    importStoriesSource: s.importStoriesSource,
    importStoriesFolderId: s.importStoriesFolderId,
    setImportStoriesSource: s.setImportStoriesSource,
    setImportStoriesFolderId: s.setImportStoriesFolderId,
    clearImportStoriesSelectedStory: s.clearImportStoriesSelectedStory,
  }));

  const activeCategoryId = useSlidesLibraryStoreShallow(
    (s) => s.activeTemplateCategoryId,
  );

  const scrollToSection = useScrollToCategory();

  const [isMyStoriesOpen, setIsMyStoriesOpen] = useState(true);

  const folders = useMemo(() => {
    if (!foldersData) return [];
    return buildNestedFolders(foldersData, undefined);
  }, [foldersData]);

  const categories = useMemo(() => {
    const raw = (templatesData as { categories?: { name: string }[] })
      ?.categories;
    if (!raw) return undefined;
    return [...raw].sort(
      (a, b) =>
        getImportStoriesCategoryRank(a.name) -
        getImportStoriesCategoryRank(b.name),
    );
  }, [templatesData]);

  const handleMyStoriesClick = () => {
    setImportStoriesSource("my-stories");
    setImportStoriesFolderId("");
    clearImportStoriesSelectedStory();
  };

  const handleFolderClick = (folderId: string) => {
    setImportStoriesSource("my-stories");
    setImportStoriesFolderId(folderId);
    clearImportStoriesSelectedStory();
  };

  const handleCategoryClick = (categoryName: string) => {
    setImportStoriesSource("templates");
    clearImportStoriesSelectedStory();
    scrollToSection(categoryName);
  };

  return (
    <ScrollArea className="tw-h-full">
      <div className="tw-flex tw-flex-col tw-gap-4 tw-px-3 tw-py-4">
        {/* My stories section — folder navigation */}
        <div>
          <div
            onClick={handleMyStoriesClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleMyStoriesClick();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="My stories"
            className={cn(
              "tw-group tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-rounded-md tw-font-sans tw-transition-colors focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200",
              importStoriesSource === "my-stories" && !importStoriesFolderId
                ? "tw-bg-blue-100"
                : "hover:tw-bg-blue-100",
            )}
          >
            <div className="tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-pl-2 tw-text-sm tw-font-normal tw-text-blue">
              <IconUser
                size={16}
                stroke={1}
                className="tw-shrink-0 tw-fill-none tw-text-blue"
              />
              <span>My stories</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-0.5 tw-pr-1">
              <button
                type="button"
                className="tw-flex tw-size-6 tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMyStoriesOpen(!isMyStoriesOpen);
                }}
              >
                <IconChevronRight
                  className={cn(
                    "tw-size-3.5 tw-text-blue tw-opacity-50 tw-transition-transform",
                    isMyStoriesOpen && "tw-rotate-90",
                  )}
                  stroke={1.5}
                />
              </button>
            </div>
          </div>

          <div
            className={cn(
              "tw-grid tw-transition-[grid-template-rows] tw-duration-200",
              isMyStoriesOpen
                ? "[grid-template-rows:1fr]"
                : "[grid-template-rows:0fr]",
            )}
          >
            <div className="tw-overflow-hidden">
              <div className="tw-mt-0.5">
                {foldersLoading ? (
                  <SlidesLibrarySidebarSkeleton />
                ) : (
                  <SortableFolderList
                    folders={folders}
                    activeFolderId={importStoriesFolderId}
                    onFolderClick={handleFolderClick}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Storydoc templates section — scroll-to navigation */}
        {!isCreatorLite && (
          <div>
            <span className="tw-block tw-px-2 tw-py-1.5 tw-font-sans tw-text-xs tw-font-normal tw-text-blue-500">
              Storydoc Templates
            </span>
            <div className="tw-mt-0.5 tw-flex tw-flex-col tw-gap-0.5">
              {templatesLoading ? (
                <SlidesLibrarySidebarSkeleton />
              ) : (
                categories?.map((category: { name: string }) => {
                  const label = getImportStoriesCategoryLabel(category.name);
                  const isActive =
                    importStoriesSource === "templates" &&
                    activeCategoryId === category.name;
                  return (
                    <button
                      key={category.name}
                      type="button"
                      onClick={() => handleCategoryClick(category.name)}
                      className={cn(
                        "tw-flex tw-h-8 tw-w-full tw-cursor-pointer tw-items-center tw-rounded-md tw-border-none tw-bg-transparent tw-px-2 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-100",
                        isActive && "tw-bg-blue-100",
                    )}
                  >
                    {label}
                  </button>
                );
              })
            )}
          </div>
        </div>
        )}
      </div>
    </ScrollArea>
  );
};

// ── Drag-and-drop folder list ──────────────────────────────

const SortableFolderList = ({
  folders,
  activeFolderId,
  onFolderClick,
}: {
  folders: FolderWithChildren[];
  activeFolderId: string;
  onFolderClick: (id: string) => void;
}) => {
  const { mutate: reorderFolders } = useReorderFoldersMutation();
  const [localFolders, setLocalFolders] = useState(folders);

  useEffect(() => {
    setLocalFolders(folders);
  }, [folders]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = localFolders.findIndex((f) => f._id === active.id);
      const newIndex = localFolders.findIndex((f) => f._id === over.id);
      const newOrder = arrayMove(localFolders, oldIndex, newIndex);
      setLocalFolders(newOrder);
      reorderFolders(newOrder.map((f) => f._id));
    }
  };

  if (localFolders.length <= 1) {
    return (
      <>
        {localFolders.map((folder) => (
          <RecursiveStoryFolder
            key={folder._id}
            folder={folder}
            level={0}
            activeFolderId={activeFolderId}
            onFolderClick={onFolderClick}
          />
        ))}
      </>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={localFolders.map((f) => f._id)}
        strategy={verticalListSortingStrategy}
      >
        {localFolders.map((folder) => (
          <SortableStoryFolder
            key={folder._id}
            folder={folder}
            level={0}
            activeFolderId={activeFolderId}
            onFolderClick={onFolderClick}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

const SortableStoryFolder = ({
  folder,
  level,
  activeFolderId,
  onFolderClick,
}: {
  folder: FolderWithChildren;
  level: number;
  activeFolderId: string;
  onFolderClick: (id: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: folder._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <RecursiveStoryFolder
        folder={folder}
        level={level}
        activeFolderId={activeFolderId}
        onFolderClick={onFolderClick}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

const RecursiveStoryFolder = ({
  folder,
  level,
  activeFolderId,
  onFolderClick,
  isDragging,
  dragHandleProps,
}: {
  folder: FolderWithChildren;
  level: number;
  activeFolderId: string;
  onFolderClick: (id: string) => void;
  isDragging?: boolean;
  dragHandleProps?: Record<string, unknown>;
}) => {
  const isActive = activeFolderId === folder._id;
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const paddingLeft = 24 + level * 16;
  const hasChildren = folder.children.length > 0;

  return (
    <div style={{ opacity: isDragging ? 0.5 : 1 }}>
      <div
        onClick={() => onFolderClick(folder._id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onFolderClick(folder._id);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={folder.name}
        aria-pressed={isActive}
        className={cn(
          "tw-group/folder tw-relative tw-mx-1 tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-pr-1 tw-font-sans tw-text-[14px] tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-100 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200",
          isActive || isMenuOpen ? "tw-bg-blue-100" : "",
        )}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            className={cn(
              "tw-absolute tw-left-0.5 tw-top-1/2 tw-invisible tw-cursor-grab -tw-translate-y-1/2 group-hover/folder:tw-visible active:tw-cursor-grabbing",
              isActive && "tw-visible",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <IconGripVertical
              className="tw-size-3 tw-text-blue-500"
              stroke={1}
            />
          </span>
        )}

        <IconFolder
          className="tw-size-4 tw-shrink-0 tw-text-blue"
          stroke={1}
        />

        <span className="tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
          {folder.name}
        </span>

        <div
          className={cn(
            "tw-invisible tw-flex tw-items-center tw-gap-0.5 group-hover/folder:tw-visible group-focus-within/folder:tw-visible",
            (isMenuOpen || isActive) && "tw-visible",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <FolderActions
            isActive={isActive}
            isOpen={isMenuOpen}
            setIsOpen={setIsMenuOpen}
            folder={folder}
          />
          {hasChildren && (
            <button
              type="button"
              className="tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-transparent tw-p-0 tw-outline-none"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
            >
              <IconChevronRight
                className={cn(
                  "tw-size-3.5 tw-text-blue tw-opacity-50 tw-transition-transform",
                  isOpen && "tw-rotate-90",
                )}
                stroke={1.5}
              />
            </button>
          )}
        </div>
      </div>

      {hasChildren && (
        <div
          className={cn(
            "tw-grid tw-transition-[grid-template-rows] tw-duration-200",
            {
              "[grid-template-rows:1fr]": isOpen,
              "[grid-template-rows:0fr]": !isOpen,
            },
          )}
        >
          <div className="tw-overflow-hidden">
            <div className="tw-mt-0.5">
              {folder.children.map((child) => (
                <RecursiveStoryFolder
                  key={child._id}
                  folder={child}
                  level={level + 1}
                  activeFolderId={activeFolderId}
                  onFolderClick={onFolderClick}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
